import { useEffect, useState } from 'react';
import { Popup, LoadPanel } from 'devextreme-react';
import { ToolbarItem } from 'devextreme-react/popup';
import { Button } from 'devextreme-react';
import { useScreenSize } from '../../../utils/media-query';
import { useAppSelector } from '../../../contexts/hooks';
import { calculateAge } from '../../../utils/functions';
import moment from 'moment';
import { newToast } from '../../../components';
import { useApiPatient } from '../../../api/api.patient';
import { useApiSearch } from '../../../api/api.Search';
import { shallowEqual } from 'react-redux';
import { date, orders, editPatient } from '../../../images/icons';
import { useApiOrders } from '../../../api/api.orders';

interface PatientDetailsProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  patient: any; // Información básica del paciente seleccionado (solo para obtener el ID)
}

export const PatientDetails = ({ visible, setVisible, patient }: PatientDetailsProps) => {
  const { isXSmall, isSmall } = useScreenSize();
  const [loading, setLoading] = useState(false);
  const [patientHistory, setPatientHistory] = useState<any[]>([]);
  const [fullPatientData, setFullPatientData] = useState<any>(null);
  const { searchPatient } = useApiPatient();
  const { GetPatientById } = useApiSearch();
  const { findOrder } = useApiOrders();

  // Obtener configuración de campos del sistema
  const info = useAppSelector((x) => x.configuration.Data?.SystemFields)
    ?.filter((x) => x.Dfc_Tabla === 'Pacientes' && x.Dfc_Estado === true && x.Dfc_Visible)
    .sort((a, b) => a.Dfc_Orden - b.Dfc_Orden);

  const cmb = useAppSelector((x) => x.configuration.Data?.ComboBox);

  // Configuración de módulos
  const ModuleSchedule = JSON.parse(useAppSelector((x) => x.configuration.Data?.Config_General.find((x) => x.Cgn_Nombre === 'ModuleSchedule'), shallowEqual)?.Cgn_Resultado || 'false');

  // useEffect para llamar a la API cada vez que se abre la modal
  useEffect(() => {
    if (visible && patient) {
      setLoading(true);

      // Buscar datos completos del paciente y su historial
      const fetchPatientData = async () => {
        try {
          const [patientData, historyData] = await Promise.all([searchPatient(patient.Pc_Id), GetPatientById(patient.Pc_Id)]);

          if (patientData) {
            setFullPatientData(patientData);
          } else {
            setFullPatientData(null);
            newToast('WARNING', 'No se pudieron cargar los datos completos del paciente', 4);
          }

          if (historyData) {
            setPatientHistory(historyData);
          } else {
            setPatientHistory([]);
          }
        } catch (error) {
          setFullPatientData(null);
          setPatientHistory([]);
          newToast('ERROR', 'Error al cargar los datos del paciente', 4);
        } finally {
          setLoading(false);
        }
      };

      fetchPatientData();
    }
  }, [visible, patient]);

  // Función para obtener el icono según el tipo de acción y código
  const getActionIcon = (tipoAccion: string, accionCodigo: string) => {
    const codigo = parseInt(accionCodigo);

    switch (codigo) {
      case 1: // Creación/Edición paciente
        return (
          <img
            src={editPatient}
            alt='Patient icon'
            style={{
              width: '20px',
              height: '20px',
            }}
          />
        );
      case 2: // Órdenes
        return (
          <img
            src={orders}
            alt='Orders icon'
            style={{
              width: '20px',
              height: '20px',
            }}
          />
        );
      case 3: // Citas
        return (
          <img
            src={date}
            alt='Date icon'
            style={{
              width: '20px',
              height: '20px',
            }}
          />
        );
      default: // Otros eventos
        return (
          <img
            src={editPatient}
            alt='Event icon'
            style={{
              width: '20px',
              height: '20px',
            }}
          />
        );
    }
  };

  // Función para obtener el color según el código de acción (colores más sobrios)
  const getActionColor = (accionCodigo: string) => {
    const codigo = parseInt(accionCodigo);

    switch (codigo) {
      case 1: // Creación paciente
        return '#52c41a';
      case 2: // Órdenes
        return '#1890ff';
      case 3: // Citas
        return '#fa8c16';
      case 4: // Resultados
        return '#13c2c2';
      case 5: // Modificaciones
        return '#fadb14';
      case 6: // Facturación
        return '#722ed1';
      default:
        return '#8c8c8c';
    }
  };

  // Función para manejar acciones (ir a orden/cita)
  const handleActionClick = async (item: any) => {
    const codigo = parseInt(item.AccionCodigo);

    if (codigo === 2) {
      setLoading(true);
      try {
        await findOrder(undefined, item.AccionId, () => {});
      } catch (error) {
        newToast('ERROR', 'Error al cargar la orden', 4);
      } finally {
        setLoading(false);
      }
    } else if (codigo === 3) {
      // Ir a citas
      newToast('INFO', `Navegando a la cita ID: ${item.AccionId}`, 3);
      // Aquí iría la lógica para navegar a la cita específica
    }
  };

  // Función para determinar si el evento tiene acción
  const hasAction = (accionCodigo: string) => {
    const codigo = parseInt(accionCodigo);
    return codigo === 2 || codigo === 3; // Órdenes o Citas
  };

  const renderPatientField = (data: any, field: string) => {
    let textRender;
    if (fullPatientData) {
      if (data.Dfc_Dinamico && fullPatientData.AditionalFields) {
        textRender = fullPatientData.AditionalFields[field];
      } else {
        textRender = fullPatientData[field];
      }
    }

    // Manejo especial para fechas
    if (data.TipoCampo?.Tcm_Equivalencia === 'dxDateBox' && textRender) {
      const formattedDate = moment(textRender).format('DD/MM/YYYY');

      // Si es fecha de nacimiento, agregar la edad
      if (data.Dfc_Equivalencia === 'Pc_FechaNacimiento') {
        const age = calculateAge(textRender);
        textRender = age ? `${formattedDate} | ${age} años` : formattedDate;
      } else {
        textRender = formattedDate;
      }
    }

    // Manejo para checkboxes y switches
    if ((data.TipoCampo?.Tcm_Equivalencia === 'dxSwitch' || data.TipoCampo?.Tcm_Equivalencia === 'dxCheckBox') && textRender) {
      textRender = textRender === 'true' || textRender === true ? 'SÍ' : 'NO';
    }

    // Manejo para selectbox
    if (data.TipoCampo?.Tcm_Equivalencia === 'dxSelectBox' && textRender) {
      const cmbActual = cmb?.filter((x) => x.Name === data.TablasComboBox?.Tbc_NombreTabla)[0];
      const combo = cmbActual?.Data;
      if (combo?.length && combo.length > 0) {
        const selectedItem = combo.filter((x) => x[data.TablasComboBox.Tbc_CampoId] === parseInt(textRender))[0];
        textRender = selectedItem ? selectedItem[data.TablasComboBox.Tbc_CampoVisible] : textRender;
      }
    }

    return textRender || '---';
  };

  const handleClose = () => {
    setVisible(false);
    setPatientHistory([]);
    setFullPatientData(null);
  };

  if (!patient) {
    return null;
  }

  return (
    <Popup height='auto' title='Detalles del Paciente' showCloseButton={true} onHidden={handleClose} fullScreen={isXSmall || isSmall} hideOnOutsideClick={false} width={isXSmall || isSmall ? '100%' : '90%'} maxWidth={1200} visible={visible}>
      <LoadPanel visible={loading} message='Cargando información del paciente...' showIndicator={true} showPane={true} shading={true} hideOnOutsideClick={false} />

      {/* Información del Paciente - Formato Simple */}
      <div
        style={{
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #e9ecef',
        }}>
        <h3
          style={{
            margin: '0 0 20px 0',
            color: '#495057',
            fontSize: '20px',
            fontWeight: '600',
            textAlign: 'center',
            borderBottom: '2px solid #dee2e6',
            paddingBottom: '10px',
          }}>
          Información del Paciente
        </h3>

        {info && info.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isXSmall ? '1fr' : isSmall ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
              gap: '10px 20px',
              alignItems: 'start',
            }}>
            {info.map((field) => (
              <div key={field.Dfc_Id} style={{ marginBottom: '8px' }}>
                <div
                  style={{
                    fontSize: '12px',
                    color: '#6c757d',
                    fontWeight: '500',
                    marginBottom: '2px',
                  }}>
                  {field.Dfc_Label}:
                </div>
                <div
                  style={{
                    fontSize: '14px',
                    color: '#495057',
                    fontWeight: '400',
                    wordBreak: 'break-word',
                  }}>
                  {renderPatientField(field, field.Dfc_Equivalencia)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              textAlign: 'center',
              color: '#6c757d',
              fontStyle: 'italic',
              padding: '40px 20px',
            }}>
            {fullPatientData ? (
              <div>
                <div style={{ marginBottom: '20px' }}>No hay campos configurados en SystemFields para pacientes</div>
                <div style={{ color: '#495057' }}>
                  <div>
                    <strong>Datos disponibles del paciente:</strong>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: isXSmall ? '1fr' : 'repeat(2, 1fr)',
                      gap: '10px',
                      marginTop: '10px',
                      textAlign: 'left',
                    }}>
                    {Object.entries(fullPatientData)
                      .filter(([key, value]) => typeof value !== 'object' || value === null || Array.isArray(value) || value instanceof Date)
                      .map(([key, value]) => (
                        <div key={key} style={{ marginBottom: '8px' }}>
                          <div style={{ fontSize: '12px', color: '#6c757d' }}>{key}:</div>
                          <div style={{ fontSize: '14px', color: '#495057' }}>{String(value)}</div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            ) : (
              'Cargando información del paciente...'
            )}
          </div>
        )}
      </div>

      {/* Línea de Tiempo del Paciente */}
      <div
        style={{
          padding: '20px',
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          border: '1px solid #e9ecef',
        }}>
        <h3
          style={{
            margin: '0 0 20px 0',
            color: '#495057',
            fontSize: '18px',
            fontWeight: '600',
            textAlign: 'center',
            borderBottom: '2px solid #dee2e6',
            paddingBottom: '10px',
          }}>
          Historial del Paciente
        </h3>

        {loading ? (
          <div
            style={{
              textAlign: 'center',
              color: '#6c757d',
              fontStyle: 'italic',
              padding: '40px 20px',
            }}>
            Cargando historial del paciente...
          </div>
        ) : patientHistory && patientHistory.length > 0 ? (
          <div
            style={{
              maxHeight: '400px',
              overflowY: 'auto',
              paddingRight: '10px',
            }}>
            {/* Timeline Container */}
            <div style={{ position: 'relative' }}>
              {/* Línea vertical principal - centrada con los círculos */}
              <div
                style={{
                  position: 'absolute',
                  left: '39px', // Centro exacto de los círculos
                  top: '0',
                  bottom: '0',
                  width: '2px',
                  backgroundColor: '#dee2e6',
                }}
              />

              {/* Items del timeline */}
              {patientHistory
                .sort((a, b) => new Date(b.Fecha).getTime() - new Date(a.Fecha).getTime())
                .map((item, index) => (
                  <div
                    key={`timeline-${item.AccionId || 'unknown'}-${item.AccionCodigo || 'unknown'}-${item.Fecha || 'unknown'}-${index}`}
                    style={{
                      position: 'relative',
                      marginLeft: '70px',
                      marginBottom: index === patientHistory.length - 1 ? '0' : '20px',
                      paddingBottom: '15px',
                    }}>
                    {/* Círculo del evento - grande, solo borde de color, fondo blanco para tapar la línea */}
                    <div
                      style={{
                        position: 'absolute',
                        left: '-50px',
                        top: '8px',
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `4px solid ${getActionColor(item.AccionCodigo)}`,
                        zIndex: 2,
                        cursor: hasAction(item.AccionCodigo) ? 'pointer' : 'default',
                      }}
                      onClick={() => hasAction(item.AccionCodigo) && handleActionClick(item)}>
                      {getActionIcon(item.TipoAccion, item.AccionCodigo)}
                    </div>

                    {/* Contenido del evento - diseño más limpio y compacto */}
                    <div
                      style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        padding: '12px',
                        border: '1px solid #e9ecef',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        position: 'relative',
                      }}>
                      {/* Barra de color lateral más sutil */}
                      <div
                        style={{
                          position: 'absolute',
                          left: '0',
                          top: '0',
                          bottom: '0',
                          width: '3px',
                          backgroundColor: getActionColor(item.AccionCodigo),
                          borderRadius: '0 8px 8px 0',
                        }}
                      />

                      {/* Header del evento */}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '6px',
                          marginLeft: '8px',
                        }}>
                        <div
                          style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#495057',
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}>
                          <span>{item.TipoAccion}</span>
                          {hasAction(item.AccionCodigo) && (
                            <button
                              onClick={() => handleActionClick(item)}
                              style={{
                                fontSize: '10px',
                                backgroundColor: getActionColor(item.AccionCodigo),
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontWeight: '400',
                                border: 'none',
                                cursor: 'pointer',
                              }}>
                              {parseInt(item.AccionCodigo) === 2 ? 'Ver Orden' : 'Ver Cita'}
                            </button>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: '12px',
                            color: '#6c757d',
                            fontWeight: '500',
                            whiteSpace: 'nowrap',
                            marginLeft: '15px',
                          }}>
                          {moment(item.Fecha).format('DD/MM/YYYY hh:mm A')}
                        </div>
                      </div>

                      {/* Detalle del evento */}
                      <div
                        style={{
                          fontSize: '13px',
                          color: '#6c757d',
                          lineHeight: '1.4',
                          marginLeft: '8px',
                        }}>
                        {item.Detalle}
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {/* Resumen estadístico simplificado */}
            <div
              style={{
                marginTop: '20px',
                display: 'grid',
                gridTemplateColumns: isXSmall ? '1fr' : ModuleSchedule ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
                gap: '10px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
              }}>
              {/* Total de eventos */}
              <div
                style={{
                  textAlign: 'center',
                  padding: '10px',
                }}>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#495057' }}>{patientHistory.length}</div>
                <div style={{ fontSize: '11px', color: '#6c757d' }}>Eventos Totales</div>
              </div>

              {/* Órdenes */}
              <div
                style={{
                  textAlign: 'center',
                  padding: '10px',
                }}>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#1890ff' }}>{patientHistory.filter((item) => parseInt(item.AccionCodigo) === 2).length}</div>
                <div style={{ fontSize: '11px', color: '#6c757d' }}>Órdenes</div>
              </div>

              {/* Citas - solo si el módulo está habilitado */}
              {ModuleSchedule && (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '10px',
                  }}>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#fa8c16' }}>{patientHistory.filter((item) => parseInt(item.AccionCodigo) === 3).length}</div>
                  <div style={{ fontSize: '11px', color: '#6c757d' }}>Citas</div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            style={{
              textAlign: 'center',
              color: '#6c757d',
              fontStyle: 'italic',
              padding: '40px 20px',
            }}>
            <div style={{ fontSize: '16px', marginBottom: '10px' }}>Sin historial disponible</div>
            <div style={{ fontSize: '14px' }}>No se encontraron eventos registrados para este paciente</div>
          </div>
        )}
      </div>

      {/* Botones del footer */}
      <ToolbarItem toolbar='bottom' location='after'>
        <Button text='Cerrar' stylingMode='contained' onClick={handleClose} className='cancel-button' />
      </ToolbarItem>
    </Popup>
  );
};
